import { useState, useEffect, useRef } from 'react';
import { parseWcWeight, suggestWeightClass, deriveAgeGroupFromYear } from '../../utils/tournament';
import {
  getTeamAthletes, getWeightClasses, createAthleteRecord, registerAthlete, updateAthleteRecord, coachEditAthlete, updateRegistration, deleteRegistration,
} from '../../api/client';
import { useI18n } from '../../i18n';
import { fillTemplate } from '../../lib/fillTemplate';
import { refusalOf } from '../../lib/httpError';
import { useLabels } from '../../i18n/labels';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../hooks/useConfirm';
import { useAfterWrite } from '../../hooks/useAfterWrite';
import { useBusy, useBusyKeys } from '../../hooks/useBusy';
import { inlineError } from '../../hooks/useApiCall';
import Avatar from '../shared/Avatar';
import GenderBadge from '../shared/GenderBadge';
import AthleteFieldsGroup, { EMPTY_RECORD_FORM, MAX_EXPERIENCE_BOUTS, toCreateRecordDto, type AthleteFieldsForm } from '../shared/AthleteFieldsGroup';
import ExistingAthleteNotice, { identityMatchOf, type IdentityMatch } from '../shared/ExistingAthleteNotice';
import { useAuthStore } from '../../store/auth';
import { logBackgroundError } from '../../observability';
import { useDeferredLoading } from '../../hooks/useDeferredLoading';
import ModalBackdrop from '../shared/ModalBackdrop';
import ModalHeader from '../shared/ModalHeader';
import ModalBody from '../shared/ModalBody';
import ModalFooter from '../shared/ModalFooter';
import ErrorState from '../shared/ErrorState';
import { CheckIcon, WarningIcon, ChevronLeftIcon, ChevronDownIcon, ChevronRightIcon } from '../shared/Icons';
import { personName, nameMatches } from '../../utils/personName';
import { weightText } from '../../utils/weight';
import type { Team, Registration, AthleteEntry } from '../../types';
import Notice from '../shared/Notice';
import Field from '../shared/Field';
import { BADGE_XXS } from '../shared/badgeSize';

// ── Age group derivation ───────────────────────────────────────────────────────
// The derive is `deriveAgeGroupFromYear()` in utils/tournament.ts, the mirror
// of the backend derive, with the allowed age groups of the tournament (#532).

// #529 — a tournament manager receives `birth_year` in place of the birth
// date. The birth date is a `YYYY-MM-DD` string (#410): read its year from
// the text, never through a Date.
function birthYearOf(a: { birth_date?: string | null; birth_year?: number | null }): number | null {
  if (a.birth_date) return Number(a.birth_date.slice(0, 4));
  return a.birth_year ?? null;
}

function suggestWc(weight: number | string | null | undefined, wcs: string[]): string {
  // NUMERIC arrives as a string ("70.0"): compare the number.
  const kg = Number(weight);
  if (!(kg > 0) || !wcs.length) return '';
  // #458 — the shared rule; when no class fits, the heaviest class stays the
  // suggestion here, as before.
  return suggestWeightClass(kg, wcs)
    ?? [...wcs].sort((a, b) => parseWcWeight(a) - parseWcWeight(b))[wcs.length - 1];
}

// ── Types ──────────────────────────────────────────────────────────────────────
/** Local row type — extends the shared `AthleteEntry` with modal-local
 *  computed fields (ageGroup derived from the year of birth — `birth_date`
 *  or, for a tournament manager, `birth_year` (#529) — + tournament date;
 *  alreadyReg cross-checked against current registrations). */
interface AthRow extends AthleteEntry {
  ageGroup: string | null;
  alreadyReg: boolean;
  /** #532 — the athlete fits an age group, but the tournament takes none of
   *  the groups the athlete is eligible for. The row stays, disabled, with
   *  the reason: a hidden athlete reads as lost data. */
  notAllowedGroup: string | null;
}

interface AthState {
  checked: boolean; expanded: boolean; wc: string; wcs: string[]; weightKg: string;
  /** #674 — the text of the bout-count field. */
  experience: string;
  /** #674 — the reason of the last refused save of the bout count. It shows under the field until the
   *  next change of the field: a toast hides after 3 s. */
  experienceError?: string;
}

/** #674 — the text of the bout-count field: the stored count, or '' when the record has none. */
function experienceText(a: { experience_bouts?: number | null }): string {
  return a.experience_bouts != null ? String(a.experience_bouts) : '';
}

interface Props {
  tournamentId: string;
  tournamentDate: string; // date_start — used for age group calculation
  /** #532 — the age groups the tournament takes; null: every age group. */
  allowedAgeGroups: string[] | null;
  teams: Team[];
  registrations: Registration[];
  onClose: () => void;
  /** The parent reads the tournament again. The read rejects when it fails (#664). */
  onDone: (addedCount: number) => Promise<unknown>;
}

export default function BulkRegisterModal({ tournamentId, tournamentDate, allowedAgeGroups, teams, registrations, onClose, onDone }: Props) {
  // #664 — the competition year, read from the day string (a `Date` of a day string is UTC midnight, #410).
  const competitionYear = Number(tournamentDate.slice(0, 4));
  const { t } = useI18n();
  const { ageShort, boutCount } = useLabels();
  const { showError, showSuccess, showToast } = useToast();
  const { confirm } = useConfirm();
  const afterWrite = useAfterWrite();
  // #605 — a lock for each row. The class select, the weight field and the
  // unregister tap of one athlete send one write at a time.
  const wcSaves = useBusyKeys();
  const weightSaves = useBusyKeys();
  // #674 — the bout count has its own lock. A save of the weight that runs does not stop it.
  const experienceSaves = useBusyKeys();
  const unregisters = useBusyKeys();
  // #605 — the number of the newest write to the athlete list. A new team or
  // a new create gives a new number. The reload of an older create then
  // writes nothing.
  const listSeq = useRef(0);
  const b = t.bulkReg;
  const singleTeam = teams.length === 1;
  const [step, setStep] = useState<1 | 2 | 3>(singleTeam ? 2 : 1);
  const [teamId, setTeamId] = useState(singleTeam ? teams[0].id : '');
  const [athletes, setAthletes] = useState<AthRow[]>([]);
  const [states, setStates] = useState<Record<string, AthState>>({});
  const [loading, setLoading] = useState(false);
  // #665 (bug 89) — the read of the team athletes failed. The list stays empty, and "No athletes
  // found" would tell the coach that the team has no boxer. `loadTick` is the Retry: it runs the effect again.
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadTick, setLoadTick] = useState(0);
  // #458 — the loading text shows after 200 ms and holds 400 ms.
  const showLoading = useDeferredLoading(loading);
  const [searchQ, setSearchQ] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  // #552 — the "New athlete" sub-view takes the focus when it opens.
  const createFirstRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (showCreate) createFirstRef.current?.focus({ preventScroll: true });
  }, [showCreate]);
  const [createForm, setCreateForm] = useState<AthleteFieldsForm>(EMPTY_RECORD_FORM);
  const [createErr, setCreateErr] = useState('');
  const { hasRole } = useAuthStore();
  // #674 — only an admin or a team manager can lower the bout count. A coach can keep or raise it.
  // The name and the roles are those of the backend check (`canLowerExperience` in
  // lib/athleteExperience.ts, #406). The server decides: it answers 403.
  const canLowerExperience = hasRole('admin', 'team_manager');
  // #658 — the record of the same person that the create found.
  const [createMatch, setCreateMatch] = useState<IdentityMatch | null>(null);
  // The notice names the record of the typed name and birth date: a new value drops it.
  useEffect(() => { setCreateMatch(null); }, [createForm.first_name, createForm.last_name, createForm.birth_date]);
  // #605 — the lock of the create. It ends at the answer of the POST.
  const createBusy = useBusy();

  // #605, #664 — the lock of Register: the loop ends it in `finally`, also when the loop throws.
  const submit = useBusy();
  // The reason of each athlete that the last submit did not register, by id.
  const [failures, setFailures] = useState<Record<string, string>>({});

  // #571 — the footer key gives each step a new Save button. The focused Save
  // then leaves the page, and the focus goes to <body>. The old button kept
  // the focus while it stayed enabled. So when Save had the focus at Next or
  // Review, the new Save gets the focus if it is enabled. Back, the step bar
  // and the submit leave the focus as the old code did: a repeated Enter on
  // Back must not go forward.
  const saveRef = useRef<HTMLButtonElement>(null);
  const focusNewSave = useRef(false);
  useEffect(() => {
    const save = saveRef.current;
    const wanted = focusNewSave.current;
    focusNewSave.current = false;
    if (wanted && save && !save.disabled && (!document.activeElement || document.activeElement === document.body)) {
      save.focus({ preventScroll: true });
    }
  }, [step, submit.busy]);
  /** Next or Review: go to the next step, and note if Save has the focus. */
  const nextStep = (n: 2 | 3) => {
    focusNewSave.current = document.activeElement === saveRef.current;
    setStep(n);
  };

  /** #532 — the age group of an athlete for THIS tournament, and the default
   *  group when the tournament takes none that the athlete fits. */
  function rowOf(a: AthleteEntry): AthRow {
    const year = birthYearOf(a);
    const ageGroup = deriveAgeGroupFromYear(year, competitionYear, allowedAgeGroups);
    const byDefault = deriveAgeGroupFromYear(year, competitionYear);
    return { ...a, ageGroup, notAllowedGroup: !ageGroup && byDefault ? byDefault : null, alreadyReg: regRecordIds.has(a.id) };
  }

  // Single canonical pointer post users-athlete-split — every registration has athlete_record_id.
  const regRecordIds = new Set(registrations.map(r => r.athlete_record_id));
  // Map AR id → registration id for unregister
  const regIdMap = new Map<string, string>();
  registrations.forEach(r => { regIdMap.set(r.athlete_record_id, r.id); });

  // ── Load athletes when team changes ──────────────────────────────────────────
  useEffect(() => {
    if (!teamId) return;
    // Only the latest run writes the state. StrictMode runs this effect twice
    // in dev, and a team change can start a load before the last one answers:
    // the late answer of a discarded run cleared "Select all" after the coach
    // ticked it (e2e-championship E2E 3, 2026-09-24).
    let live = true;
    // #605 — a new team also stops the reload of an older create.
    listSeq.current++;
    setLoading(true); setLoadFailed(false); setAthletes([]); setStates({});

    getTeamAthletes(teamId).then(async res => {
      if (!live) return;
      const rows: AthRow[] = res.data.map(rowOf);

      const combos = new Set(
        rows.filter(r => !r.alreadyReg && r.ageGroup && r.gender).map(r => `${r.ageGroup}|${r.gender}`)
      );
      const wcMap: Record<string, string[]> = {};
      // #559 — a failed load leaves the class list of that group empty. The
      // form goes on, and the person sees why the list is empty (one toast).
      let wcFailure: unknown = null;
      await Promise.all([...combos].map(async combo => {
        const [ag, gender] = combo.split('|');
        try { const r = await getWeightClasses({ age_group: ag, gender: gender as 'Male' | 'Female' }); wcMap[combo] = r.data.map((wc) => wc.class_label); }
        catch (err) { wcMap[combo] = []; wcFailure ??= err; }
      }));

      // Load weight classes for ALL athletes (including already-registered)
      const allCombos = new Set(
        rows.filter(r => r.ageGroup && r.gender).map(r => `${r.ageGroup}|${r.gender}`)
      );
      for (const combo of allCombos) {
        if (!wcMap[combo]) {
          const [ag, gender] = combo.split('|');
          try { const r = await getWeightClasses({ age_group: ag, gender: gender as 'Male' | 'Female' }); wcMap[combo] = r.data.map((wc) => wc.class_label); }
          catch (err) { wcMap[combo] = []; wcFailure ??= err; }
        }
      }
      if (wcFailure) showError(wcFailure);

      const initStates: Record<string, AthState> = {};
      for (const a of rows) {
        const combo = a.ageGroup && a.gender ? `${a.ageGroup}|${a.gender}` : '';
        const wcs = combo ? (wcMap[combo] || []) : [];
        if (a.alreadyReg) {
          // Find current weight class from registration (single canonical pointer)
          const reg = registrations.find(r => r.athlete_record_id === a.id);
          initStates[a.id] = { checked: true, expanded: false, wc: reg?.weight_class || suggestWc(a.current_weight_kg, wcs), wcs, weightKg: a.current_weight_kg ? String(a.current_weight_kg) : '', experience: experienceText(a) };
        } else {
          initStates[a.id] = { checked: false, expanded: false, wc: suggestWc(a.current_weight_kg, wcs), wcs, weightKg: a.current_weight_kg ? String(a.current_weight_kg) : '', experience: experienceText(a) };
        }
      }
      if (!live) return;
      setAthletes(rows); setStates(initStates);
    }).catch(err => {
      logBackgroundError('BulkRegisterModal:team-athletes')(err);
      if (live) setLoadFailed(true);
    }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  // We intentionally don't re-fetch when `registrations` changes — the modal
  // is a one-shot UX: open → pick team → register → close. If the parent's
  // registrations change mid-modal (e.g. a socket-driven refresh), the
  // existing athlete list stays stable; the parent's subsequent onDone()
  // callback reloads the page-level state. Adding registrations / regUserIds
  // / regRecordIds / regIdMap to deps would thrash the fetch on every
  // parent refresh.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, loadTick]);

  function patchState(id: string, patch: Partial<AthState>) {
    setStates(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }
  // Update weight class — for registered athletes, also PATCH the registration in DB
  async function changeWc(athleteId: string, newWc: string) {
    const athlete = athletes.find(a => a.id === athleteId);
    const regId = athlete?.alreadyReg ? regIdMap.get(athleteId) : undefined;
    if (!athlete || !regId) { patchState(athleteId, { wc: newWc }); return; }
    // #605 — the select of the row is disabled while its PATCH runs. Two quick
    // picks sent two PATCHes, and the server could keep the older pick.
    await wcSaves.run(athleteId, async () => {
      patchState(athleteId, { wc: newWc });
      try {
        const res = await updateRegistration(tournamentId, regId, { weight_class: newWc });
        showSuccess(t.bulkReg.weightClassSaved.replace('{name}', personName(athlete.first_name, athlete.last_name)).replace('{weightClass}', String(res.data.weight_class)));
      } catch (err) {
        showError(err);
        // Revert local state on failure
        const reg = registrations.find(r => r.id === regId);
        if (reg) patchState(athleteId, { wc: reg.weight_class });
      }
    });
  }
  // Update weight — re-suggest weight class
  function handleWeightChange(id: string, val: string) {
    const st = states[id];
    const kg = parseFloat(val);
    const newWc = !isNaN(kg) && kg > 0 ? suggestWc(kg, st.wcs) : st.wc;
    patchState(id, { weightKg: val, wc: newWc });
  }

  // Save weight to athlete record on blur
  async function saveWeight(id: string) {
    // #674 — only a coach of the team, a team manager or an admin can write the weight of the record.
    // For another person, for example an assigned tournament manager, the modal sends the value of the
    // field only with the registration (handleSubmit). The blur sends nothing: the server refuses that save with 403.
    if (!coachesThisTeam) return;
    const st = states[id];
    const athlete = athletes.find(a => a.id === id);
    if (!athlete || !st.weightKg) return;
    const kg = parseFloat(st.weightKg);
    if (isNaN(kg) || kg === Number(athlete.current_weight_kg)) return;
    // #605 — one save for each row at a time, and the field is disabled until the
    // answer. A blur while a save runs sends nothing. Two blurs sent two
    // PATCHes, and the screen and the server could keep different weights.
    await weightSaves.run(id, async () => {
      try {
        if (athlete.source === 'record' || !athlete.linked_user_id) {
          await updateAthleteRecord(id, { current_weight_kg: kg });
        } else {
          // #457 — coach-edit takes the USER id. `id` is the athlete record id,
          // so a claimed athlete answered 403 on every blur.
          await coachEditAthlete(athlete.linked_user_id, { current_weight_kg: kg });
        }
        // Update local athlete data
        setAthletes(prev => prev.map(a => a.id === id ? { ...a, current_weight_kg: kg } : a));
        showSuccess(t.bulkReg.weightSaved.replace('{name}', personName(athlete.first_name, athlete.last_name)).replace('{weight}', String(kg)));
      } catch (err) { showError(err); }   // a refused weight (400 INVALID_WEIGHT) was swallowed here (2026-09-19)
    });
  }

  // #674 — save the bout count on blur. It uses the same two paths as the weight, and a lock of its
  // own for each row. Only an admin or a team manager can lower the count. For a coach, a lower
  // count sends nothing: the field shows the stored count again. The reason shows under the field
  // until the next change of the field. The server decides too. A verdict can add 1 while the modal
  // is open. The server then refuses the count with 403, and its answer gives the stored count.
  async function saveExperience(id: string) {
    const st = states[id];
    const athlete = athletes.find(a => a.id === id);
    if (!athlete || !st) return;
    const stored = athlete.experience_bouts ?? null;   // null: the record has no count; the server counts it as 0
    const typed = st.experience.trim();
    const next = typed === '' ? NaN : Number(typed);
    // A field that is empty, or that holds the stored count, sends nothing. The field shows the stored count again.
    if (!Number.isFinite(next) || next === stored) { patchState(id, { experience: experienceText(athlete) }); return; }
    if (!canLowerExperience && next < (stored ?? 0)) {
      patchState(id, {
        experience: experienceText(athlete),
        experienceError: fillTemplate(t.errors.EXPERIENCE_DECREASE_NOT_ALLOWED, { current: stored ?? 0 }, t),
      });
      return;
    }
    await experienceSaves.run(id, async () => {
      try {
        if (athlete.source === 'record' || !athlete.linked_user_id) {
          await updateAthleteRecord(id, { experience_bouts: next });
        } else {
          await coachEditAthlete(athlete.linked_user_id, { experience_bouts: next });
        }
      } catch (err) {
        // The stored count of a 403 replaces the count of the list: the field and its `min` show it.
        const refusal = refusalOf(err);
        const current = refusal?.code === 'EXPERIENCE_DECREASE_NOT_ALLOWED' ? Number(refusal.params?.current) : NaN;
        if (Number.isFinite(current)) setAthletes(prev => prev.map(a => a.id === id ? { ...a, experience_bouts: current } : a));
        patchState(id, {
          experience: Number.isFinite(current) ? String(current) : experienceText(athlete),
          experienceError: inlineError(err),
        });
        return;
      }
      // Update local athlete data
      setAthletes(prev => prev.map(a => a.id === id ? { ...a, experience_bouts: next } : a));
      patchState(id, { experience: String(next) });
      showSuccess(t.bulkReg.experienceSaved.replace('{name}', personName(athlete.first_name, athlete.last_name)).replace('{bouts}', boutCount(next)));
      // The save also changes the count of the open registrations (syncOpenRegistrations), so the page
      // reads them again. The modal does not wait for the read. A failed read is a warning (#419, #664).
      void afterWrite({ source: 'BulkRegisterModal:experience', refresh: () => onDone(0) });
    });
  }

  async function toggleCheck(id: string) {
    const cur = states[id];
    const athlete = athletes.find(a => a.id === id);
    // #532 — the tournament does not take the age group of this athlete.
    if (!cur.checked && athlete?.notAllowedGroup) return;
    // Unchecking a registered athlete → unregister via API
    if (cur.checked && athlete?.alreadyReg) {
      const regId = regIdMap.get(id);
      if (!regId) return;
      // #665 — the question comes before run(), so the lock is free while the person reads.
      if (!(await confirm({ message: t.tournament.removeRegistrationConfirm, confirmLabel: t.tournament.removeRegistration, destructive: true }))) return;
      // #605 — one DELETE for each row. A tap while it runs does nothing, and
      // the box is disabled. A tap on the row removed a boxer with no confirmation, so
      // the person confirms first.
      await unregisters.run(id, async () => {
        try {
          await deleteRegistration(tournamentId, regId);
          // Mark as no longer registered locally
          setAthletes(prev => prev.map(a => a.id === id ? { ...a, alreadyReg: false } : a));
          patchState(id, { checked: false, expanded: false });
          // The modal does not wait for the read of the parent. A failed read is a warning (#419, #664).
          void afterWrite({ source: 'BulkRegisterModal:unregister', refresh: () => onDone(0) });
        } catch (err) {
          // #458 — the shared toast (it shows above the modal: z-60 over
          // z-50). The modal's own pill chose its colour by `includes('✓')`.
          showError(err);
        }
      });
      return;
    }
    patchState(id, { checked: !cur.checked, expanded: !cur.checked ? true : cur.expanded });
  }
  function toggleAll(val: boolean) {
    // Only toggle non-registered athletes (don't mass-unregister)
    setStates(prev => {
      const next = { ...prev };
      athletes.filter(a => !a.alreadyReg && !a.notAllowedGroup).forEach(a => {
        next[a.id] = { ...next[a.id], checked: val, expanded: val || next[a.id].expanded };
      });
      return next;
    });
  }

  // Only newly checked (not already registered) athletes go to review
  const selectedAthletes = athletes.filter(a => !a.alreadyReg && states[a.id]?.checked);
  const allValid = selectedAthletes.every(a => states[a.id]?.wc && a.ageGroup);
  const available = athletes.filter(a => !a.alreadyReg && !a.notAllowedGroup);
  const availCount = available.length;
  const allChecked = availCount > 0 && available.every(a => states[a.id]?.checked);
  const selectedTeam = teams.find(c => c.id === teamId);
  // #674 — the person who can write the weight and the bout count of an athlete of the selected team.
  // This is an admin, a team manager, or an approved coach of the team. `my_role` holds only the
  // APPROVED seat of the caller (routes/teams.ts). The server has the same rule (MUST_BE_TEAM_COACH,
  // MUST_BE_ATHLETE_TEAM_COACH), and TeamDetail gives this role set the same name (#406). Another
  // person, for example an assigned tournament manager, sees the bout count as text. That person
  // gets the weight field for a row that is not registered, because the registration needs a weight
  // (WEIGHT_REQUIRED). A registered row shows the weight as text (`weightAsText`). The blur saves
  // the weight in the record only for this role set (saveWeight).
  const coachesThisTeam = hasRole('admin', 'team_manager') || !!selectedTeam?.my_role;

  const filtered = athletes.filter(a => nameMatches(searchQ, a.first_name, a.last_name));

  // ── Create athlete ────────────────────────────────────────────────────────────
  async function handleCreate(e: React.FormEvent) {
    e.preventDefault(); setCreateErr(''); setCreateMatch(null);
    const { first_name, last_name, gender, birth_date } = createForm;
    if (!first_name || !last_name || !gender || !birth_date) { setCreateErr(b.genderRequired); return; }
    const newYear = birthYearOf({ birth_date });
    const byDefault = deriveAgeGroupFromYear(newYear, competitionYear);
    if (!byDefault) { setCreateErr(b.ageOutOfRange); return; }
    // #532 — the tournament does not take the age group of the new athlete.
    if (!deriveAgeGroupFromYear(newYear, competitionYear, allowedAgeGroups)) {
      setCreateErr(b.ageGroupNotTaken.replace('{ageGroup}', byDefault)); return;
    }
    const created = await createBusy.run(async () => {
      try {
        await createAthleteRecord(teamId, toCreateRecordDto(createForm));
        return true;
      } catch (err) {
        const match = identityMatchOf(err);
        if (match) { setCreateMatch(match); return false; }
        setCreateErr(inlineError(err, b.createFailed)); return false;
      }
    });
    if (!created) return;
    // #605 — the record exists now, so the overlay closes at once. A failed
    // reload showed as a create error, and a second try got 409
    // ATHLETE_IDENTITY_EXISTS. Now it is a warning (#419).
    setShowCreate(false);
    setCreateForm(EMPTY_RECORD_FORM);
    const seq = ++listSeq.current;
    await afterWrite({
      source: 'BulkRegisterModal:create-athlete',
      refresh: async () => {
        const res = await getTeamAthletes(teamId);
        if (seq !== listSeq.current) return;
        const rows: AthRow[] = res.data.map(rowOf);
        const added: Record<string, AthState> = {};
        // #559 — a failed load left the class list empty with no message.
        let wcFailure: unknown = null;
        for (const a of rows) {
          if (!a.alreadyReg && !states[a.id]) {
            let wcs: string[] = [];
            if (a.ageGroup && a.gender) {
              try { const r = await getWeightClasses({ age_group: a.ageGroup, gender: a.gender as 'Male' | 'Female' }); wcs = r.data.map((wc) => wc.class_label); }
              catch (err) { wcFailure ??= err; }
            }
            added[a.id] = { checked: true, expanded: true, wc: suggestWc(a.current_weight_kg, wcs), wcs, weightKg: a.current_weight_kg ? String(a.current_weight_kg) : '', experience: experienceText(a) };
          }
        }
        if (seq !== listSeq.current) return;
        if (wcFailure) showError(wcFailure);
        // #605 — the list is open during the reload: a tick of the coach stays.
        // #665 — this refresh is a second writer of the list: a good answer ends an earlier load failure.
        setAthletes(rows); setLoadFailed(false); setStates(prev => ({ ...added, ...prev }));
      },
    });
  }

  // ── Submit ────────────────────────────────────────────────────────────────────
  // The result is ONE global toast with the numbers, not a list of every
  // athlete (operator, 2026-09-25). When every athlete is registered, the
  // modal closes. When one fails, the modal stays on the review step with
  // the failed athletes only, each with its reason, so the coach can fix it
  // and register again. The toast shows for 3 s, so a reason is never only
  // in the toast.
  async function handleSubmit() {
    const failed: Record<string, string> = {};
    let added = 0;
    const ran = await submit.run(async () => {
      for (const a of selectedAthletes) {
        try {
          const weightStr = states[a.id]?.weightKg;
          const kg = weightStr ? parseFloat(weightStr) : NaN;
          // #364 — the registration weight is mandatory. Report it on the
          // athlete's row instead of sending a request the API answers
          // 400 WEIGHT_REQUIRED to.
          if (!isFinite(kg) || kg <= 0) {
            failed[a.id] = t.tournament.registrationWeightRequired;
            continue;
          }
          await registerAthlete(tournamentId, {
            athlete_record_id: a.id, // single canonical pointer post users-athlete-split
            team_id: teamId, age_group: a.ageGroup!, weight_class: states[a.id].wc,
            current_weight_kg: kg,
          });
          added++;
          // Registered: it leaves the selection, so a new submit sends only the failed ones.
          setAthletes(prev => prev.map(x => x.id === a.id ? { ...x, alreadyReg: true } : x));
        } catch (err) { failed[a.id] = inlineError(err); }
      }
      return true;
    });
    if (!ran) return;
    setFailures(failed);
    const notAdded = Object.keys(failed).length;
    // The modal does not wait for the read of the parent. A failed read is a warning (#419, #664).
    if (added > 0) void afterWrite({ source: 'BulkRegisterModal:register', refresh: () => onDone(added) });
    if (notAdded === 0) {
      showSuccess((b.registeredCount ?? '').replace('{n}', String(added)));
      onClose();
      return;
    }
    showToast(
      added > 0
        ? (b.registeredPartly ?? '').replace('{ok}', String(added)).replace('{failed}', String(notAdded))
        : (b.noneRegistered ?? '').replace('{failed}', String(notAdded)),
      added > 0 ? 'warning' : 'error',
    );
  }

  const stepLabels = [b.stepTeam, b.stepAthletes, b.stepReview];

  // #552 — the shared panel at a fixed height (90dvh): the footer stays above
  // the toolbar of a phone browser (100vh hid it), and the height does not
  // jump between the steps. Esc and the backdrop close the "New athlete"
  // sub-view first; they closed the whole wizard and lost every tick.
  return (
    <ModalBackdrop onClose={showCreate ? () => setShowCreate(false) : onClose} maxWidth="5xl" layout="panel" fullHeight
      ariaLabel={t.modalLabels.bulkRegisterAria}>
        <ModalHeader title={b.title} />

        {/* Step bar */}
        <div className="flex border-b border-gray-100 bg-gray-50 shrink-0 overflow-x-auto no-scrollbar">
          {stepLabels.map((label: string, i: number) => {
            const n = (i + 1) as 1 | 2 | 3;
            const isActive = step === n;
            const isDone = step > n;
            return (
              <button key={n} data-testid={`bulk-step-${n}`} disabled={submit.busy}
                onClick={() => { if (isDone) setStep(n); else if (n === 2 && teamId) setStep(2); else if (n === 3 && selectedAthletes.length > 0) setStep(3); }}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 -mb-px transition-colors shrink-0 whitespace-nowrap ${
                  isActive ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-400'}`}>
                <span className={`w-4 h-4 rounded-full text-[10px] flex items-center justify-center border font-medium ${
                  isDone ? 'bg-gray-900 border-gray-900 text-white' : isActive ? 'border-gray-900 text-gray-900' : 'border-gray-300 text-gray-400'}`}>
                  {isDone ? <CheckIcon className="w-2.5 h-2.5" /> : n}
                </span>
                {label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <ModalBody>

          {/* Step 1: Team */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">{b.whichTeam}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {teams.map(c => (
                  <button key={c.id} onClick={() => setTeamId(c.id)} data-testid={`bulk-team-${c.id}`}
                    className={`text-left border rounded-xl p-3 flex items-center gap-3 transition-all ${
                      teamId === c.id ? 'border-2 border-gray-900' : 'border-gray-200 hover:border-gray-400'}`}>
                    <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm shrink-0">
                      {c.name.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{c.name}</div>
                      <div className="text-xs text-gray-400 truncate">{c.location}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Athletes */}
          {step === 2 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input type="text" placeholder={b.searchAthletes} value={searchQ} onChange={e => setSearchQ(e.target.value)}
                  className="input flex-1 text-sm py-1.5" />
                <button onClick={() => { setShowCreate(true); setCreateErr(''); setCreateMatch(null); setCreateForm(EMPTY_RECORD_FORM); }}
                  className="btn-outline btn-sm whitespace-nowrap" data-testid="bulk-new-athlete">{b.newAthlete}</button>
              </div>

              {showLoading && <p className="text-sm text-gray-400 text-center py-4">{b.loading}</p>}

              {!loading && loadFailed && <ErrorState compact onRetry={() => setLoadTick(n => n + 1)} />}

              {!loading && availCount > 0 && (
                <label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer select-none">
                  <input type="checkbox" checked={allChecked} onChange={e => toggleAll(e.target.checked)} className="w-3.5 h-3.5" />
                  {b.selectAll} ({availCount})
                </label>
              )}

              <div className="space-y-1.5">
                {filtered.map(a => {
                  const st = states[a.id];
                  if (!st) return null;
                  const isCk = st?.checked ?? false;
                  const isExp = st?.expanded ?? false;
                  const ag = a.ageGroup;
                  const kg = weightText(st?.weightKg || a.current_weight_kg);
                  // #674 — a registered row is not in the submit (selectedAthletes). For a person who does not
                  // coach the team, the blur does not save the record either (saveWeight). A weight field then
                  // shows a change that the page never sends. So that person sees the weight as text, in the
                  // same way as the bout count.
                  const weightAsText = a.alreadyReg && !coachesThisTeam;
                  const shownKg = Number(st.weightKg);

                  return (
                    <div key={a.id} data-testid={`bulk-athlete-${a.id}`} className={`border rounded-xl overflow-hidden transition-colors ${
                      isCk ? 'border-gray-900' : 'border-gray-200'} ${a.notAllowedGroup ? 'opacity-60' : ''}`}>
                      <div className={`flex items-center gap-2.5 px-3 py-2.5 ${a.notAllowedGroup ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}
                        onClick={() => toggleCheck(a.id)}>
                        <input type="checkbox" checked={isCk} disabled={(!!a.notAllowedGroup && !isCk) || unregisters.isBusy(a.id)}
                          onChange={() => toggleCheck(a.id)}
                          onClick={e => e.stopPropagation()} className="w-3.5 h-3.5 shrink-0" />
                        <Avatar firstName={a.first_name} lastName={a.last_name} photoUrl={a.photo_url} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium flex items-center gap-1.5 flex-wrap">
                            {personName(a.first_name, a.last_name)}
                            <GenderBadge gender={a.gender ?? ''} short size="xs" />
                          </div>
                          <div className="text-xs text-gray-400">
                            {a.age ? ageShort(a.age) : ''}
                            {kg ? ` · ${kg}` : ''}
                            {a.experience_bouts != null ? ` · ${boutCount(a.experience_bouts)}` : ''}
                          </div>
                          {a.notAllowedGroup && (
                            <div data-testid="bulk-age-group-not-taken" className="text-xs text-amber-700">
                              {b.ageGroupNotTaken.replace('{ageGroup}', a.notAllowedGroup)}
                            </div>
                          )}
                        </div>
                        {a.alreadyReg && isCk && <span className={`badge badge-green ${BADGE_XXS} shrink-0`}>{b.registered}</span>}
                        {isCk && (
                          <span onClick={e => { e.stopPropagation(); patchState(a.id, { expanded: !isExp }); }}
                            className="text-gray-400 hover:text-gray-700 text-base leading-none shrink-0 cursor-pointer">
                            <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${isExp ? 'rotate-180' : ''}`} />
                          </span>
                        )}
                      </div>

                      {/* Expanded fields */}
                      {isCk && isExp && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-3 pb-3 pt-2 bg-gray-50 border-t border-gray-100">
                          <div>
                            <div className="text-[10px] text-gray-500 mb-1">{b.ageGroup}</div>
                            {ag ? <span className="badge badge-amber text-xs">{ag}</span>
                              : <span className="text-red-500 text-xs">{b.needsBirthDate}</span>}
                            <div className="text-[10px] text-gray-400 mt-1">{b.derivedFromBirth}</div>
                          </div>
                          <Field label={b.weightClass} required labelClassName="text-[10px] text-gray-500 mb-1 block"
                            htmlFor={`bulk-wc-${a.id}`} group={st.wcs.length === 0}>
                            {st.wcs.length > 0 ? (
                              <select id={`bulk-wc-${a.id}`} className="input text-xs py-1" value={st.wc} onChange={e => changeWc(a.id, e.target.value)}
                                disabled={wcSaves.isBusy(a.id)}>
                                <option value="">— {t.common.select} —</option>
                                {st.wcs.map(w => <option key={w} value={w}>{w}</option>)}
                              </select>
                            ) : <div className="text-xs text-gray-400">{ag ? b.noClassesFound : b.noAgeGroup}</div>}
                            {st.wc && <div className="text-[10px] text-green-600 mt-1">{b.suggestedFromWeight}</div>}
                            {st.wcs.length > 0 && !st.wc && <div className="text-[10px] text-red-500 mt-1">{b.required}</div>}
                          </Field>
                          {/* #674 — a row that is not registered gets the weight field for every person. Its value
                              is the weight of the registration (WEIGHT_REQUIRED). On blur, saveWeight also writes the
                              weight of the record, but only for `coachesThisTeam`. For another person, the blur sends
                              nothing. A registered row shows the weight as text for that person (`weightAsText`).
                              The weight field and the count field are 36 px tall on a phone (`max-sm:min-h-9`),
                              the same height as `btn-sm`. */}
                          <Field label={`${b.currentWeight} (kg)`} labelClassName="text-[10px] text-gray-500 mb-1 block"
                            htmlFor={`bulk-weight-${a.id}`} group={weightAsText}>
                            {weightAsText ? (
                              <div className="text-xs text-gray-700" data-testid={`bulk-weight-text-${a.id}`}>
                                {shownKg > 0 ? shownKg : '—'}
                              </div>
                            ) : (
                              <>
                                <input type="number" min="20" max="200" step="0.1" required id={`bulk-weight-${a.id}`}
                                  className="input text-xs py-1 w-20 max-sm:min-h-9"
                                  value={st.weightKg}
                                  onChange={e => handleWeightChange(a.id, e.target.value)}
                                  onBlur={() => saveWeight(a.id)}
                                  disabled={weightSaves.isBusy(a.id)}
                                  placeholder="kg"
                                  data-testid={`bulk-weight-${a.id}`} />
                                {st.weightKg && parseFloat(st.weightKg) !== Number(a.current_weight_kg) && (
                                  <div className="text-[10px] text-orange-500 mt-0.5">{a.current_weight_kg ? t.modalLabels.weightWas.replace('{n}', String(a.current_weight_kg)) : t.modalLabels.weightNew}</div>
                                )}
                              </>
                            )}
                          </Field>
                          {/* #674 — beside the weight. The bout count is a field only for a person who can write it
                              (`coachesThisTeam`). Another person sees the stored count as text: the server refuses
                              the save of that person with 403. The `min` is a hint: a coach cannot go under the stored count. */}
                          <Field label={t.profile.experienceBouts} labelClassName="text-[10px] text-gray-500 mb-1 block"
                            htmlFor={`bulk-experience-${a.id}`} group={!coachesThisTeam}>
                            {coachesThisTeam ? (
                              <input type="number" min={canLowerExperience ? 0 : (a.experience_bouts ?? 0)} max={MAX_EXPERIENCE_BOUTS}
                                className="input text-xs py-1 w-20 max-sm:min-h-9"
                                value={st.experience}
                                onChange={e => patchState(a.id, { experience: e.target.value, experienceError: undefined })}
                                onBlur={() => saveExperience(a.id)}
                                disabled={experienceSaves.isBusy(a.id)}
                                id={`bulk-experience-${a.id}`}
                                data-testid={`bulk-experience-${a.id}`} />
                            ) : (
                              <div className="text-xs text-gray-700" data-testid={`bulk-experience-text-${a.id}`}>
                                {a.experience_bouts ?? '—'}
                              </div>
                            )}
                          </Field>
                          {/* #674 — the reason of a refused bout count, under the fields and as wide as the row. */}
                          {coachesThisTeam && st.experienceError && (
                            <Notice tone="error" plain size="xs" className="col-span-2 sm:col-span-4"
                              data-testid={`bulk-experience-error-${a.id}`}>{st.experienceError}</Notice>
                          )}
                        </div>
                      )}

                      {/* Collapsed summary */}
                      {isCk && !isExp && (
                        <div className="flex items-center gap-2 px-3 pb-2 pt-1 bg-gray-50 border-t border-gray-100">
                          {ag && <span className={`badge badge-amber ${BADGE_XXS}`}>{ag}</span>}
                          <span className={`badge ${BADGE_XXS} ${st.wc ? 'badge-gray' : 'bg-red-50 text-red-700 border-red-200'}`}>
                            {st.wc ? st.wc : `${b.weightClass} ${b.required?.toLowerCase()}`}
                          </span>
                          <button onClick={e => { e.stopPropagation(); patchState(a.id, { expanded: true }); }}
                            className="text-[10px] text-blue-600 hover:text-blue-800 ml-auto">{b.edit}</button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {!loading && !loadFailed && filtered.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-6">{b.noAthletesFound}</p>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">{b.confirmFor} <strong>{selectedTeam?.name}</strong></p>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-gray-200">
                    <th className="text-left pb-2 font-medium">{b.athlete}</th>
                    <th className="text-left pb-2 font-medium">{b.ageGroup}</th>
                    <th className="text-left pb-2 font-medium">{b.weightClass}</th>
                    <th className="text-left pb-2 font-medium">{t.common.gender}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {selectedAthletes.map(a => (
                    <tr key={a.id}>
                      <td className="py-2.5">
                        <div className="font-medium">{personName(a.first_name, a.last_name)}</div>
                        {failures[a.id] && <Notice tone="error" plain size="xs" data-testid={`register-failed-${a.id}`}>{failures[a.id]}</Notice>}
                        <div className="text-xs text-gray-400">
                          {a.age ? ageShort(a.age) : ''}
                          {a.experience_bouts != null ? ` · ${boutCount(a.experience_bouts)}` : ''}
                        </div>
                      </td>
                      <td className="py-2.5"><span className="badge badge-amber text-xs">{a.ageGroup}</span></td>
                      <td className="py-2.5"><span className="badge badge-gray text-xs">{states[a.id]?.wc}</span></td>
                      <td className="py-2.5">
                        <GenderBadge gender={a.gender ?? ''} size="sm" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!allValid && (
                <Notice tone="warning">{b.missingFields}</Notice>
              )}
            </div>
          )}
        </ModalBody>

        {/* Footer: [Back][count] on the left, [Next|Review|Register N][Cancel] on the right.
            #571 — the key gives each step, and the busy state, new buttons. Before,
            one button served all three steps. Its label and its disabled state
            changed in one frame, while .btn faded its opacity. iOS Safari then kept
            the old "Next →" under the new "Review →". A new button starts no transition. */}
        <ModalFooter
          key={submit.busy ? 'submitting' : step}
          saveRef={saveRef}
          secondary={<>
            {step === 2 && !singleTeam && <button type="button" onClick={() => setStep(1)} className="btn-outline btn-sm"><ChevronLeftIcon className="w-3.5 h-3.5" />{b.back}</button>}
            {/* #571 — no step change while the submit runs: the busy key would keep one Save node. */}
            {step === 3 && <button type="button" onClick={() => setStep(2)} disabled={submit.busy} className="btn-outline btn-sm"><ChevronLeftIcon className="w-3.5 h-3.5" />{b.back}</button>}
            {step === 2 && selectedAthletes.length > 0 && <span className="text-sm text-gray-500"><strong>{selectedAthletes.length}</strong> {b.selected}</span>}
            {step === 3 && <span className="text-sm text-gray-500">{b.registering} <strong>{selectedAthletes.length}</strong></span>}
          </>}
          onSave={step === 1 ? () => { if (teamId) nextStep(2); } : step === 2 ? () => nextStep(3) : handleSubmit}
          busy={submit.busy}
          busyLabel={b.submitting}
          saveLabel={step === 1 ? <>{b.next}<ChevronRightIcon className="w-3.5 h-3.5" /></> : step === 2 ? <>{b.review}<ChevronRightIcon className="w-3.5 h-3.5" /></> : `${b.register} ${selectedAthletes.length}`}
          saveDisabled={step === 1 ? !teamId : step === 2 ? selectedAthletes.length === 0 : !allValid}
          onCancel={onClose}
          cancelLabel={b.cancel}
        />

        {/* Create athlete overlay */}
        {showCreate && (
          <div className="absolute inset-0 bg-white rounded-2xl z-10 flex flex-col">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 shrink-0">
              <button type="button" onClick={() => setShowCreate(false)} aria-label={b.back} title={b.back} className="btn-icon">
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <h3 className="font-bold text-lg leading-tight flex-1">{b.newAthleteTitle}</h3>
            </div>
            <ModalBody className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500 leading-relaxed">
                {b.newAthleteDesc}{' '}<strong className="text-gray-700">{selectedTeam?.name}</strong>.{' '}{b.newAthleteNote}
              </div>
              <form id="bulk-create-form" onSubmit={handleCreate} className="space-y-3">
                <AthleteFieldsGroup form={createForm} onChange={setCreateForm} required firstFieldRef={createFirstRef}
                  testIds={{ first: 'new-athlete-first_name', last: 'new-athlete-last_name' }} />
                {createErr && <Notice tone="error" plain>{createErr}</Notice>}
                {createMatch && (
                  <ExistingAthleteNotice match={createMatch} teamId={teamId}
                    canInvite={coachesThisTeam}
                    onInvited={() => { setCreateMatch(null); setShowCreate(false); }} />
                )}
              </form>
            </ModalBody>
            <ModalFooter
              saveAsSubmit
              saveForm="bulk-create-form"
              busy={createBusy.busy}
              busyLabel={b.creating}
              saveLabel={b.createAndAdd}
              onCancel={() => setShowCreate(false)}
              cancelLabel={b.cancel}
            />
          </div>
        )}
    </ModalBackdrop>
  );
}
